import React from 'react';

export function getRoadmapNotePlainText(raw) {
    if (!raw) return '';

    try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        const walk = (node) => {
            if (!node) return '';
            if (node.type === 'linebreak') return '\n';
            if (node.type === 'text') return node.text ?? '';
            const content = (node.children ?? []).map(walk).join('');
            return ['paragraph', 'heading', 'listitem', 'quote', 'code'].includes(node.type)
                ? `${content}\n`
                : content;
        };
        return walk(parsed?.root ?? parsed).replace(/\s+/g, ' ').trim();
    } catch {
        return String(raw).replace(/\s+/g, ' ').trim();
    }
}

function alignmentClass(format) {
    if (format === 'center') return 'text-center';
    if (format === 'right') return 'text-right';
    if (format === 'justify') return 'text-justify';
    return '';
}

function LexicalNode({node}) {
    if (!node) return null;
    const children = node.children?.map((child, index) => <LexicalNode key={child.key ?? index} node={child}/>) ?? null;

    switch (node.type) {
        case 'root': return <div className="flex flex-col gap-3">{children}</div>;
        case 'paragraph': return <p className={`whitespace-pre-wrap text-sm leading-7 text-[var(--text-secondary)] ${alignmentClass(node.format)}`}>{children?.length ? children : <br/>}</p>;
        case 'heading': {
            const Tag = /^h[1-6]$/.test(node.tag) ? node.tag : 'h2';
            return <Tag className={`font-bold text-[var(--text-primary)] ${Tag === 'h1' ? 'text-2xl' : Tag === 'h2' ? 'text-xl' : 'text-lg'} ${alignmentClass(node.format)}`}>{children}</Tag>;
        }
        case 'text': {
            const format = node.format ?? 0;
            let content = node.text ?? '';
            if (format & 16) content = <code className="rounded bg-[var(--surface-deep)] px-1 py-0.5 font-mono text-xs text-[var(--brand-coral)]">{content}</code>;
            if (format & 1) content = <strong className="font-semibold text-[var(--text-primary)]">{content}</strong>;
            if (format & 2) content = <em>{content}</em>;
            if (format & 8) content = <u>{content}</u>;
            if (format & 4) content = <s>{content}</s>;
            return content;
        }
        case 'link': return <a href={node.url} target="_blank" rel="noreferrer" className="text-[var(--brand-coral)] underline">{children}</a>;
        case 'list': return node.listType === 'bullet'
            ? <ul className="list-inside list-disc space-y-1 text-[var(--text-secondary)]">{children}</ul>
            : <ol className="list-inside list-decimal space-y-1 text-[var(--text-secondary)]">{children}</ol>;
        case 'listitem': return <li className="leading-7">{children}</li>;
        case 'quote': return <blockquote className="border-l-4 border-[var(--brand-coral)]/50 pl-4 italic text-[var(--text-muted)]">{children}</blockquote>;
        case 'code': return <pre className="overflow-x-auto rounded-xl border border-[var(--border-color)] bg-[var(--surface-deep)] p-4 font-mono text-xs text-[var(--brand-coral)]">{children}</pre>;
        case 'linebreak': return <br/>;
        default: return children ? <span>{children}</span> : null;
    }
}

export default function RoadmapNoteContent({content}) {
    if (!content) return null;
    try {
        const parsed = typeof content === 'string' ? JSON.parse(content) : content;
        return <LexicalNode node={parsed?.root ?? parsed}/>;
    } catch {
        return <p className="whitespace-pre-wrap text-sm leading-7 text-[var(--text-secondary)]">{String(content)}</p>;
    }
}
